/**
 * The DEFAULT-VALUE number coercers: unknown in, a number out, `safeDefault` whenever the input carries no number.
 * `QUAL-INLINE-NUMBER-COERCION-001` names `coerceNumber` as the owner of the string-parsing contract; the nullable
 * contract is `parseNumberLike` (`const/parse-number-like.ts`), deliberately not merged (a blank or unparseable input
 * answers `null` there and `safeDefault` here).
 *
 * HOMED IN `const/` (moved from `utils/data/` on 2026-09-27) because that is the floor every caller can reach: a pure
 * `utils/` leaf may not value-import a `utils/` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist), and
 * the deposit and buying-power readers (`utils/property/deposit-calculation.ts`, `utils/users/buying-power.ts`) are
 * utils leaves that read these. While the pair sat one floor up, those two readers were forced to stay `tools/` leaves
 * and every business surface importing them was a `QUAL-NO-NONFLOW-TOOL-IMPORTS-001` row. Its own value import is one
 * const sibling, so the leaf is const-clean.
 *
 * ONE LEAF FOR BOTH: the formatted coercer is the plain one over a separator-stripped string, so it COMPOSES
 * `coerceNumber` rather than re-deriving the parse-and-fallback; two leaves would have had to duplicate that body,
 * since this floor may not import a sibling either way.
 */

import { nanToDefault } from '@federa/const/number-guards';

/** Coerce unknown input to a number: numbers pass through (NaN falls to `safeDefault`), numeric strings parse through
 *  `Number.parseFloat`, everything else answers `safeDefault`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): utils readers that may not import a utils sibling reach it here. */
export function coerceNumber(value: unknown, safeDefault: number = 0): number {
	if (typeof value === 'number') return nanToDefault(value, safeDefault);
	if (typeof value === 'string') return nanToDefault(Number.parseFloat(value), safeDefault);
	return safeDefault;
}

/** {@link coerceNumber} over a string with its thousands separators (commas, whitespace) stripped first; a string that
 *  is blank once stripped answers `safeDefault`, never the `0` that `Number('')` would.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): same layer-floor grounds as {@link coerceNumber} above. */
export function coerceFormattedNumber(value: unknown, safeDefault: number = 0): number {
	if (typeof value !== 'string') return coerceNumber(value, safeDefault);
	const cleaned = value.replace(/[,\s]/g, '');
	return cleaned ? coerceNumber(cleaned, safeDefault) : safeDefault;
}
